/**
 * A session's debug stream, as a sheet over whatever you were reading.
 *
 * Stories VC-014, VC-016 and RO-021. `agent_runtime_lld.md` §30, §30.7,
 * §30.13.
 *
 * VC-014 built this as a second page. A second page is the wrong shape for
 * the question it answers — "why did *that* take four seconds" is asked
 * about a session you are looking at, and a tool you have to navigate away
 * to is a tool nobody opens. So it is a sheet: over the conversation on a
 * phone, beside it on a desktop, and full-width when asked (§30.13.2).
 *
 * # The model is not ported
 *
 * Every rule about what the frames *mean* stays in `src/debug/model.js`,
 * and every rule about where the graph puts things in `src/debug/layout.js`.
 * Neither has a DOM in it, and both have `node --test` suites. This file
 * turns their structures into elements and decides nothing about the
 * runtime (`AGENTS.md` §II.4.2).
 *
 * # Three views, one look (§30.13.3–§30.13.6)
 *
 * Every item, row and node that belongs to an agent carries that agent's
 * tag, every one carries `+Δ` from the session's first frame, and every
 * payload opens in place — under the card, row or leaf it belongs to.
 *
 * # The debug credential is not the session credential
 *
 * §30.7 makes debug permission a *separate* token, because a flag on the
 * session token "would let every holder of one read every inference request
 * in the tenant". Two parameters, so nobody can pass one for the other by
 * accident — and a deployment with no `VOICE_DEBUG_TOKEN` has no debug plane
 * at all and answers 404, which is deliberate: 403 would tell an attacker
 * where to come back to.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import {
  agentLabel,
  buildItems,
  buildModel,
  buildTree,
  delta,
  elapsed,
  parseFrame,
  short,
  since,
} from "../debug/model.js";
import { BAR_HEIGHT, LABEL_WIDTH, LANE_HEIGHT, layoutGraph } from "../debug/layout.js";
import { storedBlobIds, withResolvedBlobs } from "../debug/blobs.js";
import { readDebugBlob, readDebugEvents } from "../debug/stream.js";
import { Icon } from "./Icon.jsx";

/** Which icon a family is drawn with (§30.13.6). */
const ICON_OF_FAMILY = {
  inference: "inference",
  tool: "tool",
  response: "chat",
  child_agent: "branch",
  message: "mail",
  session: "flag",
  error: "warning",
  gap: "warning",
  agent: "agent",
  action: "send",
  history: "history",
  rehydration: "history",
  scheduling: "history",
  cancellation: "stop",
};

/** A family's icon, in the family's colour. */
function FamilyIcon({ family, size = 18 }) {
  return (
    <span className="family-icon" style={{ color: `var(--family-${family ?? "gap"})` }}>
      <Icon name={ICON_OF_FAMILY[family] ?? "pulse"} size={size} />
    </span>
  );
}

/** An agent's tag: its name and short id, with a dot in its colour. */
export function AgentTag({ model, agentId }) {
  if (!agentId) return null;
  const agent = model.agents.get(agentId) ?? { id: agentId };
  return (
    <span className="agent-tag" title={agent.id}>
      <span
        className="agent-tag__dot"
        style={{ background: `var(--agent-${agent.colour ?? 0})` }}
        aria-hidden="true"
      />
      {agentLabel(agent)}
    </span>
  );
}

/**
 * @param {{ sessionId: string, config: { base: string },
 *           debugToken: string, onClose: () => void }} props
 */
export function DebugSheet({ sessionId, config, debugToken, onClose }) {
  const [frames, setFrames] = useState([]);
  const [view, setView] = useState("timeline");
  const [problem, setProblem] = useState(null);
  const [live, setLive] = useState(false);
  const [wide, setWide] = useState(false);

  useEffect(() => {
    if (!debugToken) {
      setProblem(
        "no credential to ask with. Sign in, or pass ?debug_token= — the " +
          "second shared secret a static deployment grants debug with " +
          "(§30.7).",
      );
      return undefined;
    }
    const abort = new AbortController();
    let decoder;

    (async () => {
      // The same framing the event stream uses, from the same place: a
      // `text/event-stream` arrives in arbitrary chunks and an event is not
      // a line, so `agent-sse` is the workspace's one answer to it.
      const wasm = await import("../../pkg/sse/agent_sse.js");
      await wasm.default();
      decoder = new wasm.WasmTextEventStream();
      setLive(true);
      await readDebugEvents({ base: config.base, debugToken }, sessionId, {
        decoder,
        onFrame: (frame) =>
          setFrames((current) => [...current, parseFrame(frame)]),
        signal: abort.signal,
      });
    })()
      .catch((error) => {
        if (!abort.signal.aborted) {
          setProblem(`${error.message ?? error}`);
        }
      })
      .finally(() => {
        if (!abort.signal.aborted) setLive(false);
      });

    return () => abort.abort();
  }, [config.base, debugToken, sessionId]);

  const model = useMemo(() => buildModel(frames), [frames]);
  const access = useMemo(
    () => ({ sessionId, config, debugToken }),
    [sessionId, config, debugToken],
  );

  return (
    <>
      <div className="scrim" onClick={onClose} aria-hidden="true" />
      <aside
        className={wide ? "sheet sheet--wide" : "sheet"}
        role="dialog"
        aria-modal="true"
        aria-label={`Debug stream for session ${short(sessionId)}`}
      >
        <div className="sheet__grip" />
        <header className="sheet__head">
          <h2 className="sheet__title">Inside session {short(sessionId)}</h2>
          {live ? (
            <span className="live-chip">
              <span className="live-chip__dot" aria-hidden="true" /> live
            </span>
          ) : null}
          <button
            type="button"
            className="icon-btn sheet__widen"
            onClick={() => setWide((current) => !current)}
            aria-pressed={wide}
            aria-label={wide ? "Back to a side sheet" : "Expand to full width"}
          >
            <Icon name={wide ? "shrink" : "expand"} />
          </button>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            aria-label="Close the debug panel"
          >
            <Icon name="close" />
          </button>
        </header>

        <nav className="debug__tabs" aria-label="Debug views">
          {["timeline", "events", "tree"].map((name) => (
            <button
              type="button"
              key={name}
              className={view === name ? "chip chip--selected" : "chip"}
              onClick={() => setView(name)}
              aria-pressed={view === name}
            >
              {name}
            </button>
          ))}
          <span className="debug__count">
            {frames.length} frame{frames.length === 1 ? "" : "s"}
            {model.gaps.length ? ` · ${model.gaps.length} gap` : ""}
          </span>
        </nav>

        <div className="sheet__body">
          {problem ? (
            <p className="status status--error">{problem}</p>
          ) : frames.length === 0 ? (
            <p className="empty">
              Waiting for frames. They arrive as the session works — a session
              that is idle produces none, which is not a failure.
            </p>
          ) : (
            <DebugViews view={view} model={model} live={live} access={access} />
          )}
        </div>
      </aside>
    </>
  );
}

/**
 * The three views over one model. Exported for the render tests, which
 * drive it with a captured stream and no network.
 */
export function DebugViews({ view, model, live = false, access = {} }) {
  const built = useMemo(() => buildItems(model), [model]);
  if (view === "events") return <Events model={model} access={access} />;
  if (view === "tree") return <Tree model={model} built={built} access={access} />;
  return <Timeline model={model} built={built} live={live} access={access} />;
}

/* ------------------------------------------------------------------ */
/* timeline                                                             */

/** The clock while the stream is live, ticking once a second. */
function useNow(live) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [live]);
  return live ? now : undefined;
}

/**
 * The graph on top and the flow beneath it (§30.13.3).
 *
 * A tool, response or sub-agent sits under the pass that decided it, so a
 * pass and what it decided read as one group.
 */
function Timeline({ model, built, live, access }) {
  const [selected, setSelected] = useState(null);
  const now = useNow(live);
  const { items } = built;

  if (!items.length) {
    return <p className="empty">Nothing on the timeline yet. Frames arrive as the session works.</p>;
  }

  const passes = new Set(items.filter((item) => item.kind === "inference").map((item) => item.id));
  const ownedBy = (item) => (item.passId && passes.has(`inference:${item.passId}`) ? `inference:${item.passId}` : null);
  const top = items.filter((item) => !ownedBy(item));
  const childrenOf = (pass) => items.filter((item) => ownedBy(item) === pass.id);

  const select = (id) => {
    setSelected(id);
    // In the browser, the card comes into view; jsdom has no layout.
    document.getElementById(cardId(id))?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  };

  return (
    <div className="timeline">
      <Graph model={model} items={items} now={now} selected={selected} onSelect={select} />
      <ol className="flow">
        {top.map((item) => (
          <li key={item.id} className="flow__item">
            <Card item={item} model={model} access={access} selected={selected === item.id} onSelect={select} />
            {item.kind === "inference" && childrenOf(item).length ? (
              <ol className="flow flow--nested" aria-label="What this pass decided">
                {childrenOf(item).map((owned) => (
                  <li key={owned.id} className="flow__item">
                    <Card
                      item={owned}
                      model={model}
                      access={access}
                      selected={selected === owned.id}
                      onSelect={select}
                    />
                  </li>
                ))}
              </ol>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

function cardId(itemId) {
  return `debug-item-${String(itemId).replace(/[^a-zA-Z0-9_-]/g, "-")}`;
}

/** The lane graph: SVG, drawn from `layoutGraph`'s numbers. */
function Graph({ model, items, now, selected, onSelect }) {
  const plot = useRef(null);
  const [width, setWidth] = useState(640);
  useEffect(() => {
    const element = plot.current;
    if (!element || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.max(entry.contentRect.width, 320)));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  if (model.start === undefined) return null;
  const graph = layoutGraph({ items, start: model.start, end: model.end, now, width });

  return (
    <div className="graph" ref={plot}>
      <svg
        className="graph__svg"
        width={graph.width}
        height={graph.height}
        role="group"
        aria-label="When each thing happened"
      >
        {graph.bands.map((band) => (
          <rect
            key={band.id}
            className={band.even ? "graph__band graph__band--even" : "graph__band"}
            x={band.x}
            y={0}
            width={band.w}
            height={graph.height}
          />
        ))}
        {graph.ticks.map((tick) => (
          <g key={tick.x}>
            <line className="graph__tick" x1={tick.x} x2={tick.x} y1={14} y2={graph.height} />
            <text className="graph__tick-label" x={tick.x + 3} y={11}>
              {tick.label}
            </text>
          </g>
        ))}
        {graph.lanes.map((lane) => (
          <g key={lane.kind}>
            <line className="graph__rule" x1={LABEL_WIDTH} x2={graph.width} y1={lane.y + LANE_HEIGHT} y2={lane.y + LANE_HEIGHT} />
            <circle cx={10} cy={lane.y + LANE_HEIGHT / 2} r={4} fill={`var(--family-${lane.family})`} />
            <text className="graph__lane" x={20} y={lane.y + LANE_HEIGHT / 2 + 4}>
              {lane.label}
            </text>
          </g>
        ))}
        {graph.marks.map((mark) => (
          <rect
            key={mark.id}
            className={[
              "graph__mark",
              mark.running ? "graph__mark--running" : "",
              mark.failed ? "graph__mark--failed" : "",
              selected === mark.id ? "graph__mark--selected" : "",
            ].join(" ")}
            x={mark.x}
            y={mark.y}
            width={mark.w}
            height={mark.h}
            rx={mark.dot ? BAR_HEIGHT / 2 : 4}
            fill={`var(--family-${mark.family})`}
            tabIndex={0}
            role="button"
            aria-label={`${mark.kind}${mark.agentId ? ` · ${agentLabel(model.agents.get(mark.agentId))}` : ""}`}
            onClick={() => onSelect(mark.id)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") onSelect(mark.id);
            }}
          />
        ))}
        {graph.nowX !== undefined ? (
          <g>
            <line className="graph__now" x1={graph.nowX} x2={graph.nowX} y1={14} y2={graph.height} />
            <text className="graph__now-label" x={graph.nowX - 4} y={11} textAnchor="end">
              now
            </text>
          </g>
        ) : null}
      </svg>
    </div>
  );
}

/** One item: tinted, rounded, its frames and payloads inside (§30.13.3). */
function Card({ item, model, access, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const isOpen = open || selected;
  return (
    <article
      id={cardId(item.id)}
      className={`dcard dcard--${item.kind}${selected ? " dcard--selected" : ""}`}
      style={{ "--tint": `var(--family-${item.family})` }}
    >
      <span className="dcard__time">{delta(since(model, item.start))}</span>
      <div className="dcard__box">
        <button
          type="button"
          className="dcard__head"
          aria-expanded={isOpen}
          onClick={() => {
            setOpen(!isOpen);
            if (isOpen && selected) onSelect(null);
          }}
        >
          <FamilyIcon family={item.family} />
          <span className="dcard__title">{item.title}</span>
          <span className="dcard__who">
            <AgentTag model={model} agentId={item.agentId} />
            {item.kind === "child" && item.childAgentId ? (
              <>
                <Icon name="chevronRight" size={14} />
                <AgentTag model={model} agentId={item.childAgentId} />
              </>
            ) : null}
            {/* On a phone the +Δ column folds into the card (§30.13.3). */}
            <span className="dcard__time-inline">{delta(since(model, item.start))}</span>
          </span>
          <span className="dcard__end">
            {item.state === "running" ? (
              <span className="running">running</span>
            ) : item.state === "failed" ? (
              <span className="failed">failed</span>
            ) : item.durationMs !== undefined ? (
              elapsed(item.durationMs)
            ) : item.end !== undefined && item.end !== item.start ? (
              elapsed(item.end - item.start)
            ) : (
              ""
            )}
          </span>
          <Icon name={isOpen ? "chevronDown" : "chevronRight"} size={16} />
        </button>
        {item.summary ? <p className="dcard__summary">{item.summary}</p> : null}
        {isOpen ? (
          <ol className="frames" aria-label="Its frames">
            {item.frames.map((frame) => (
              <FrameRow key={`${frame.sequence}:${frame.event_type}`} frame={frame} model={model} access={access} compact />
            ))}
          </ol>
        ) : null}
      </div>
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* events                                                               */

/** Every frame, in arrival order; each opens in place (§30.13.4). */
function Events({ model, access }) {
  return (
    <ol className="frames frames--log">
      {model.frames.map((frame, index) =>
        frame.frame === "gap" ? (
          <li key={`gap:${index}`} className="frame frame--gap">
            <span className="frame__time" />
            <FamilyIcon family="gap" size={16} />
            <span className="frame__type">gap · {frame.missed ?? 0} missed</span>
          </li>
        ) : (
          <FrameRow key={`${frame.sequence}:${frame.event_type}:${index}`} frame={frame} model={model} access={access} />
        ),
      )}
    </ol>
  );
}

/** One frame as a row, its payload beneath it when opened. */
function FrameRow({ frame, model, access, compact = false }) {
  const [open, setOpen] = useState(false);
  return (
    <li className={open ? "frame frame--open" : "frame"} style={{ "--tint": `var(--family-${frame.family ?? "gap"})` }}>
      <button type="button" className="frame__row" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="frame__time">{delta(since(model, frame.at))}</span>
        <FamilyIcon family={frame.family} size={16} />
        <span className="frame__type">{frame.event_type}</span>
        <span className="frame__seq">#{frame.sequence}</span>
        {compact ? null : (
          <span className="frame__who">
            <AgentTag model={model} agentId={frame.agent_id} />
            {frame.turn_id ? <span className="frame__turn">turn {short(frame.turn_id)}</span> : null}
          </span>
        )}
      </button>
      {open ? <Payload frame={frame} {...access} /> : null}
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* tree                                                                 */

/** Session → agent → turn → pass → what it decided (§30.13.5). */
function Tree({ model, built, access }) {
  const nodes = useMemo(() => buildTree(model, built), [model, built]);
  if (!nodes.length) return <p className="empty">No agents in this stream yet.</p>;
  return (
    <ul className="tree" role="tree">
      {nodes.map((node) => (
        <TreeNode key={node.key} node={node} model={model} access={access} depth={0} />
      ))}
    </ul>
  );
}

function TreeNode({ node, model, access, depth }) {
  // Structure starts open; the leftovers and the frames start closed, so
  // the tree reads as the work rather than as the log.
  const [open, setOpen] = useState(node.kind !== "other" && node.kind !== "event");
  const leaf = node.kind === "event";
  const hasChildren = node.children.length > 0;
  return (
    <li
      className={`tree__node tree__node--${node.kind}`}
      role="treeitem"
      aria-expanded={hasChildren || leaf ? open : undefined}
      style={{ "--tint": `var(--family-${node.family})` }}
    >
      <button type="button" className="tree__row" onClick={() => setOpen(!open)}>
        <span className="tree__chevron">
          {hasChildren || leaf ? <Icon name={open ? "chevronDown" : "chevronRight"} size={14} /> : null}
        </span>
        <FamilyIcon family={node.kind === "agent" ? "agent" : node.family} size={16} />
        <span className="tree__label">{node.label}</span>
        {node.agentId && !leaf ? (
          // The agent's colour on every item it owns; its name on hover. A
          // frame leaf sits under its item and would only repeat it. The
          // agent node itself carries the full tag as its label.
          <span
            className="agent-tag__dot"
            title={agentLabel(model.agents.get(node.agentId) ?? { id: node.agentId })}
            style={{ background: `var(--agent-${model.agents.get(node.agentId)?.colour ?? 0})` }}
          />
        ) : null}
        {node.detail ? <span className="tree__detail">{node.detail}</span> : null}
        <span className="tree__time">{node.at !== undefined ? delta(since(model, node.at)) : ""}</span>
      </button>
      {open && leaf ? <Payload frame={node.frame} {...access} /> : null}
      {open && hasChildren ? (
        <ul className="tree__children" role="group">
          {node.children.map((child) => (
            <TreeNode key={child.key} node={child} model={model} access={access} depth={depth + 1} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* payload                                                              */

/**
 * One frame's payload, in place.
 *
 * `VOICE_DEBUG_CAPTURE` defaults to `metadata`, which keeps every event and
 * every duration and omits prompts, arguments and snapshots. Whatever
 * survives the policy is shown, and a frame whose payload was withheld says
 * so rather than rendering as an empty object (§30.6).
 */
function Payload({ frame, sessionId, config, debugToken }) {
  // Blob ids resolved for *this* frame. Reset per frame rather than kept
  // across them: two frames can reference the same payload, and a cache
  // keyed by nothing would show one frame's content under another's heading
  // the moment a checksum differed.
  const [resolved, setResolved] = useState(new Map());
  const [fetching, setFetching] = useState(false);
  const [problem, setProblem] = useState(null);

  const payload = frame?.payload;
  const references = useMemo(() => storedBlobIds(payload), [payload]);

  useEffect(() => {
    setResolved(new Map());
    setProblem(null);
    if (references.length === 0 || !sessionId || !debugToken) {
      return undefined;
    }
    const abort = new AbortController();
    setFetching(true);
    (async () => {
      const found = new Map();
      for (const blobId of references) {
        try {
          found.set(
            blobId,
            await readDebugBlob({ base: config.base, debugToken }, sessionId, blobId, {
              signal: abort.signal,
            }),
          );
        } catch (error) {
          if (abort.signal.aborted) {
            return;
          }
          // Reported once and the rest still tried: one unreadable payload
          // should not hide the others, and §30.8 lets a blob expire
          // independently of the record that names it.
          setProblem(String(error.message ?? error));
        }
      }
      if (!abort.signal.aborted) {
        setResolved(found);
        setFetching(false);
      }
    })();
    return () => {
      abort.abort();
      setFetching(false);
    };
  }, [references, sessionId, config?.base, debugToken]);

  const shown = useMemo(
    () => (resolved.size > 0 ? withResolvedBlobs(payload, resolved) : payload),
    [payload, resolved],
  );

  return (
    <div className="payload">
      {references.length > 0 ? (
        <p className="empty">
          {fetching
            ? `fetching ${references.length} stored payload${references.length === 1 ? "" : "s"}…`
            : `${resolved.size} of ${references.length} stored payload${
                references.length === 1 ? "" : "s"
              } fetched (§30.8)`}
          {problem ? ` — ${problem}` : null}
        </p>
      ) : null}
      {payload === undefined || payload === null ? (
        <p className="empty">
          The payload was withheld by the capture policy (§30.6). Raise
          VOICE_DEBUG_CAPTURE to see it.
        </p>
      ) : (
        <pre className="raw-snapshot">{JSON.stringify(shown, null, 2)}</pre>
      )}
    </div>
  );
}
